import React, { useEffect, useMemo, useState } from 'react';
import { Users, Package, IndianRupee, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import './AdminDashboard.css';
import { apiClient, endpoints } from '../api';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const [vendorId, setVendorId] = useState('');
  const [category, setCategory] = useState('');
  const [productStatus, setProductStatus] = useState('');
  const [vendorStatus, setVendorStatus] = useState('');
  const [lowStockThreshold, setLowStockThreshold] = useState('');

  const adminKey = useMemo(() => (localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey') || ''), []);
  const adminToken = useMemo(() => (localStorage.getItem('adminToken') || localStorage.getItem('token') || ''), []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        vendorId: vendorId || undefined,
        category: category || undefined,
        product_status: productStatus || undefined,
        vendor_status: vendorStatus || undefined,
        low_stock_threshold: lowStockThreshold || undefined
      };
      const { data } = await apiClient.get(endpoints.adminDashboardSummary, {
        params,
        headers: {
          ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      setSummary(data);
    } catch (e) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [vendorId, category, productStatus, vendorStatus, lowStockThreshold]);

  const cards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        title: 'Total Vendors',
        value: `${summary.vendors?.total || 0}`,
        sub: `${summary.vendors?.active || 0} active / ${summary.vendors?.inactive || 0} inactive`,
        changeType: 'neutral',
        icon: Users,
        color: 'blue'
      },
      {
        title: 'Total Products',
        value: `${summary.products?.total || 0}`,
        sub: 'Across applied filters',
        changeType: 'neutral',
        icon: Package,
        color: 'green'
      },
      {
        title: 'Available Stock',
        value: `${summary.stock?.stock_available || 0}`,
        sub: `Reserved: ${summary.stock?.stock_reserved || 0}`,
        changeType: 'neutral',
        icon: TrendingUp,
        color: 'orange'
      },
      {
        title: 'Payouts (Completed)',
        value: `₹${(summary.payouts?.completed || 0).toLocaleString()}`,
        sub: `Pending: ₹${(summary.payouts?.pending || 0).toLocaleString()} • Failed: ₹${(summary.payouts?.failed || 0).toLocaleString()}`,
        changeType: 'neutral',
        icon: IndianRupee,
        color: 'purple'
      }
    ];
  }, [summary]);

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Platform-wide inventory, pricing, and payouts at a glance.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <input placeholder="Filter by Vendor ID" value={vendorId} onChange={(e) => setVendorId(e.target.value)} />
        <input placeholder="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
        <select value={productStatus} onChange={(e) => setProductStatus(e.target.value)}>
          <option value="">Product Status</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
        <select value={vendorStatus} onChange={(e) => setVendorStatus(e.target.value)}>
          <option value="">Vendor Status</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="SUSPENDED">SUSPENDED</option>
          <option value="APPROVED">APPROVED</option>
          <option value="IN_REVIEW">IN_REVIEW</option>
        </select>
        <input placeholder="Low stock threshold" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} />
        <button className="btn btn-secondary" onClick={fetchSummary} disabled={loading}>Refresh</button>
      </div>

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <div className="admin-stats-grid">
            {cards.map((stat, index) => {
              const Icon = stat.icon;
              const changeType = stat.changeType || 'neutral';
              return (
                <div key={index} className={`admin-stat-card admin-stat-card-${stat.color}`}>
                  <div className="admin-stat-icon">
                    <Icon size={24} />
                  </div>
                  <div className="admin-stat-content">
                    <h3 className="admin-stat-title">{stat.title}</h3>
                    <p className="admin-stat-value">{stat.value}</p>
                    <div className={`admin-stat-change ${changeType}`}>
                      {changeType === 'positive' ? <TrendingUp size={16} /> : changeType === 'negative' ? <TrendingDown size={16} /> : null}
                      {stat.sub ? <span style={{ marginLeft: 6 }}>{stat.sub}</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="admin-dashboard-content">
            <div className="admin-dashboard-section">
              <div className="admin-section-header">
                <h2>Price Summary</h2>
              </div>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Average</th>
                      <th>Min</th>
                      <th>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>₹{Number(summary?.prices?.average || 0).toFixed(2)}</td>
                      <td>₹{Number(summary?.prices?.min || 0).toFixed(2)}</td>
                      <td>₹{Number(summary?.prices?.max || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-dashboard-section">
              <div className="admin-section-header">
                <h2>Low Stock Alerts</h2>
                <span className="admin-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} /> {summary?.lowStock?.length || 0}
                </span>
              </div>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Vendor ID</th>
                      <th>Category</th>
                      <th>On Hand</th>
                      <th>Reserved</th>
                      <th>Available</th>
                      <th>Min Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary?.lowStock || []).map((p) => (
                      <tr key={p.id} className={p.stock_available <= (p.min_stock_level || 0) ? 'low-stock-row' : ''}>
                        <td>{p.name || p.product_name || p.id}</td>
                        <td>{p.vendor_id}</td>
                        <td>{p.category || '-'}</td>
                        <td>{p.stock_on_hand}</td>
                        <td>{p.stock_reserved}</td>
                        <td style={{ color: '#dc2626', fontWeight: 600 }}>{p.stock_available}</td>
                        <td>{p.min_stock_level}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
